import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, ButtonGroup, Col, Form, Row } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import AbpnSpinner from "../../Layout/Components/AbpnSpinner";
import { abpnClient } from "../../Services/AbpnClient";
import { ApprovedActivitiesSearchResultSet } from "../Models/ApprovedActivity";
import { Certification } from "../Models/Certification";
import { Organization } from "../Models/Organization";
import ActivitiesTable from "./ActivitiesTable";
import OrganizationFilter from "./OrganizationFilter";
import ResultFilters from "./ResultFilters";

const filterParamNames = [
  "CertificationIds",
  "IncludeAllCertificationsActivities",
  "InstitutionIds",
  "OrganizationFilter",
  "ProductFilter",
  "CertificationFilter",
  "CmeFilter",
  "SaFilter",
  "CmeCreditsValue",
  "CmeCreditsEndValue",
  "SaCreditsValue",
  "SaCreditsEndValue",
  "PipClinicalFilter",
  "PipFeedbackFilter",
  "PatientSafetyFilter",
  "EventStartDateValue",
  "EventStartDateEndValue",
  "EventEndDateValue",
  "EventEndDateEndValue"
];

export default function ActivitiesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [resultSet, setResultSet] = useState<ApprovedActivitiesSearchResultSet>(null);
  const [loadingCertifications, setLoadingCertifications] = useState<boolean>(true);
  const [loadingOrganizations, setLoadingOrganizations] = useState<boolean>(true);
  const [loadingResults, setLoadingResults] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const resultsRegionRef = useRef<HTMLDivElement>(null);
  const pendingPageFocusRef = useRef<number | null>(null);

  const selectedCertificationIds = useMemo(() => {
    return searchParams.getAll("CertificationIds").map(_ => parseInt(_)).filter(_ => !isNaN(_));
  }, [searchParams]);

  const selectedOrganizationIds = useMemo(() => {
    return searchParams.getAll("InstitutionIds").map(_ => parseInt(_)).filter(_ => !isNaN(_));
  }, [searchParams]);

  const includeAll = searchParams.get("IncludeAllCertificationsActivities") === "true";
  const hasCertificationFilter = selectedCertificationIds.length > 0 || includeAll;
  const requestedPageNumber = parseInt(searchParams.get("PageNumber") ?? "1");
  const pageNumber = isNaN(requestedPageNumber) ? 1 : requestedPageNumber;
  const totalPages = resultSet?.totalCount ? Math.ceil(resultSet.totalCount / resultSet.pageSize) : 0;
  const activeFilterCount = filterParamNames.filter(name => searchParams.has(name) && searchParams.get(name)).length;
  const appliedFilters = useMemo(
    () => getAppliedFilters(searchParams, certifications, organizations),
    [searchParams, certifications, organizations]
  );

  useEffect(() => {
    const loadCertifications = async () => {
      setLoadingCertifications(true);
      const data = await abpnClient.get<Certification[]>("/api/activities/certifications");
      setCertifications(data);
      setLoadingCertifications(false);
    };

    loadCertifications().catch(_ => {
      setLoadingCertifications(false);
      setError("Unable to load certifications.");
    });
  }, []);

  useEffect(() => {
    const loadOrganizations = async () => {
      setLoadingOrganizations(true);
      const data = await abpnClient.get<Organization[]>("/api/activities/institutions");
      setOrganizations(data);
      setLoadingOrganizations(false);
    };

    loadOrganizations().catch(_ => {
      setLoadingOrganizations(false);
      setError("Unable to load organizations.");
    });
  }, []);

  useEffect(() => {
    if (!hasCertificationFilter) {
      setResultSet(null);
      return;
    }

    const loadResults = async () => {
      setLoadingResults(true);
      setError("");
      const query = new URLSearchParams(searchParams);
      if (!query.has("PageNumber")) query.set("PageNumber", "1");
      if (!query.has("PageSize")) query.set("PageSize", "25");
      const data = await abpnClient.get<ApprovedActivitiesSearchResultSet>(`/api/activities/search?${query.toString()}`);
      setResultSet(data);
      setLoadingResults(false);
    };

    loadResults().catch(_ => {
      setLoadingResults(false);
      setError("Unable to load approved activities.");
    });
  }, [searchParams, hasCertificationFilter]);

  useEffect(() => {
    if (pendingPageFocusRef.current === null || loadingResults || !resultSet) return;
    if (resultSet.pageNumber !== pendingPageFocusRef.current) return;

    pendingPageFocusRef.current = null;
    resultsRegionRef.current?.focus({ preventScroll: true });
    resultsRegionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [loadingResults, resultSet]);

  const updateCertificationSelection = (certificationIds: number[]) => {
    const next = new URLSearchParams(searchParams);
    next.delete("CertificationIds");
    certificationIds.forEach(id => next.append("CertificationIds", id.toString()));
    next.set("PageNumber", "1");
    setSearchParams(next);
  };

  const updateIncludeAll = (value: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set("IncludeAllCertificationsActivities", "true");
    else next.delete("IncludeAllCertificationsActivities");
    next.set("PageNumber", "1");
    setSearchParams(next);
  };

  const updateProductSearch = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set("ProductFilter", value);
    else next.delete("ProductFilter");
    next.set("PageNumber", "1");
    setSearchParams(next);
  };

  const updateOrganizationSelection = (organizationIds: number[]) => {
    const next = new URLSearchParams(searchParams);
    next.delete("InstitutionIds");
    organizationIds.forEach(id => next.append("InstitutionIds", id.toString()));
    next.set("PageNumber", "1");
    setSearchParams(next);
  };

  const clearFilters = () => {
    const next = new URLSearchParams(searchParams);
    filterParamNames.forEach(name => next.delete(name));
    [
      "CmeCreditsOperator",
      "SaCreditsOperator",
      "EventStartDateOperator",
      "EventEndDateOperator"
    ].forEach(name => next.delete(name));
    next.set("PageNumber", "1");
    setSearchParams(next);
  };

  const updatePage = (page: number) => {
    const next = new URLSearchParams(searchParams);
    next.set("PageNumber", page.toString());
    pendingPageFocusRef.current = page;
    setSearchParams(next);
  };

  return (
    <main className="mt-4">
      <section className="aa-page-shell">
        <h1 className="aa-page-title">Approved CC Activities List</h1>
        <p className="text-primary fw-bold mb-4">Continuing Certification Activities</p>

        {error && <Alert variant="danger" role="alert">{error}</Alert>}

        <Row className="g-4">
          <Col xs={12} lg={3}>
            {loadingCertifications ? <AbpnSpinner /> :
              <ResultFilters
                certifications={certifications}
                selectedCertificationIds={selectedCertificationIds}
                includeAll={includeAll}
                searchParams={searchParams}
                setSearchParams={setSearchParams}
                onCertificationChange={updateCertificationSelection}
                onIncludeAllChange={updateIncludeAll}
                onClearFilters={clearFilters}
                clearDisabled={activeFilterCount === 0}
              />
            }
          </Col>
          <Col xs={12} lg={9}>
            {!hasCertificationFilter &&
              <Alert variant="info" role="status" aria-live="polite">
                Please select a specialty and/or subspecialty and an activity type.
              </Alert>
            }

            {hasCertificationFilter &&
              <section aria-live="polite">
                <Row className="align-items-end mb-3 g-3">
                  <Col xs={12} md={6}>
                    <Form.Group controlId="ProductFilter">
                      <Form.Label className="fw-bold text-secondary">Search Activity Name</Form.Label>
                      <Form.Control
                        type="search"
                        value={searchParams.get("ProductFilter") ?? ""}
                        onChange={e => updateProductSearch(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Label className="fw-bold text-secondary" htmlFor="OrganizationFilter">Organization</Form.Label>
                    <OrganizationFilter
                      organizations={organizations}
                      selectedOrganizationIds={selectedOrganizationIds}
                      onChange={updateOrganizationSelection}
                      disabled={loadingOrganizations}
                    />
                  </Col>
                </Row>

                <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                  <AppliedFilters filters={appliedFilters} />
                  <div className="text-md-end ms-md-auto">
                    <span className="fw-bold text-secondary">
                      {resultSet ? `${resultSet.totalCount.toLocaleString()} result${resultSet.totalCount === 1 ? "" : "s"}` : "Results"}
                    </span>
                    {activeFilterCount > 0 && <span className="ms-2 text-muted">({activeFilterCount} active filter{activeFilterCount === 1 ? "" : "s"})</span>}
                  </div>
                </div>

                {loadingResults && <AbpnSpinner />}

                {!loadingResults && resultSet?.results?.length === 0 &&
                  <Alert variant="info" role="status">There are no approved activities for this selection.</Alert>
                }

                {!loadingResults && resultSet?.results?.length > 0 &&
                  <div ref={resultsRegionRef} role="region" tabIndex={-1} aria-label="Approved activities results">
                    <PaginationControls
                      currentPage={resultSet.pageNumber || pageNumber}
                      totalPages={totalPages}
                      onPageChange={updatePage}
                      className="mb-3"
                    />
                    <ActivitiesTable
                      activities={resultSet.results}
                      searchParams={searchParams}
                      setSearchParams={setSearchParams}
                    />
                    <PaginationControls
                      currentPage={resultSet.pageNumber || pageNumber}
                      totalPages={totalPages}
                      onPageChange={updatePage}
                      className="mt-3"
                    />
                  </div>
                }
              </section>
            }
          </Col>
        </Row>
      </section>
    </main>
  );
}

function AppliedFilters(props: { filters: string[] }) {
  if (props.filters.length === 0) return null;

  return (
    <div className="aa-applied-filters" aria-label="Applied filters">
      <span className="aa-applied-filters__label">Applied filters:</span>
      <ul className="aa-applied-filters__list">
        {props.filters.map(filter => <li key={filter}>{filter}</li>)}
      </ul>
    </div>
  );
}

function getAppliedFilters(
  searchParams: URLSearchParams,
  certifications: Certification[],
  organizations: Organization[]
) {
  const filters: string[] = [];
  const certificationNames = getSelectedNames(searchParams, "CertificationIds", certifications);
  const organizationNames = getSelectedNames(searchParams, "InstitutionIds", organizations);
  const productFilter = searchParams.get("ProductFilter");
  const organizationFilter = searchParams.get("OrganizationFilter");
  const certificationFilter = searchParams.get("CertificationFilter");

  if (certificationNames.length > 0) filters.push(`Certification: ${certificationNames.join(", ")}`);
  if (searchParams.get("IncludeAllCertificationsActivities") === "true") filters.push("Certification: Applies to all sub/specialties");
  if (productFilter) filters.push(`Activity name: ${productFilter}`);
  if (organizationNames.length > 0) filters.push(`Organization: ${organizationNames.join(", ")}`);
  if (organizationFilter) filters.push(`Organization: ${organizationFilter}`);
  if (certificationFilter) filters.push(`Certification name: ${certificationFilter}`);

  [
    ["CmeFilter", "CME"],
    ["SaFilter", "SA-CME"],
    ["PipClinicalFilter", "PIP Clinical"],
    ["PipFeedbackFilter", "PIP Feedback"],
    ["PatientSafetyFilter", "Patient Safety"]
  ].forEach(([parameterName, label]) => {
    if (searchParams.get(parameterName) === "true") filters.push(`Activity type: ${label}`);
  });

  addRangeFilter(filters, searchParams, "CmeCredits", "CME credits");
  addRangeFilter(filters, searchParams, "SaCredits", "SA-CME credits");
  addDateFilter(filters, searchParams, "EventStartDate", "Event begin");
  addDateFilter(filters, searchParams, "EventEndDate", "Event end");

  return filters;
}

function getSelectedNames(
  searchParams: URLSearchParams,
  parameterName: string,
  options: { id: number; name: string }[]
) {
  const selectedIds = searchParams.getAll(parameterName).map(_ => parseInt(_)).filter(_ => !isNaN(_));
  return options.filter(_ => selectedIds.includes(_.id)).map(_ => _.name);
}

function addRangeFilter(filters: string[], searchParams: URLSearchParams, prefix: string, label: string) {
  const value = searchParams.get(`${prefix}Value`);
  if (!value) return;

  const operator = searchParams.get(`${prefix}Operator`) ?? "greater";
  const endValue = searchParams.get(`${prefix}EndValue`);
  const operatorLabels: { [key: string]: string } = {
    equals: "is",
    less: "is at most",
    greater: "is at least"
  };
  filters.push(operator === "between" && endValue
    ? `${label}: ${value}–${endValue}`
    : `${label} ${operatorLabels[operator] ?? operatorLabels.greater} ${value}`);
}

function addDateFilter(filters: string[], searchParams: URLSearchParams, prefix: string, label: string) {
  const value = searchParams.get(`${prefix}Value`);
  if (!value) return;

  const operator = searchParams.get(`${prefix}Operator`) ?? "after";
  const endValue = searchParams.get(`${prefix}EndValue`);
  const operatorLabels: { [key: string]: string } = {
    after: "on or after",
    before: "on or before",
    on: "on"
  };
  filters.push(operator === "between" && endValue
    ? `${label}: ${value}–${endValue}`
    : `${label} ${operatorLabels[operator] ?? operatorLabels.after} ${value}`);
}

function PaginationControls(props: {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
}) {
  const currentPage = props.currentPage || 1;

  return (
    <div className={`d-flex justify-content-between align-items-center ${props.className ?? ""}`}>
      <span className="aa-font-size-smallest text-muted">
        Page {currentPage} of {props.totalPages}
      </span>
      <ButtonGroup aria-label="Pagination">
        <Button variant="outline-secondary" size="sm" disabled={currentPage <= 1} onClick={() => props.onPageChange(currentPage - 1)}>
          Previous
        </Button>
        <Button variant="outline-secondary" size="sm" disabled={!props.totalPages || currentPage >= props.totalPages} onClick={() => props.onPageChange(currentPage + 1)}>
          Next
        </Button>
      </ButtonGroup>
    </div>
  );
}
